// ============================================================
//  src/pages/bots/BotDetailPage.js
// ============================================================
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, NavLink, Routes, Route, Navigate } from 'react-router-dom';
import './BotDetailNav.css';
import { getBot, toggleBot, deleteBot } from '../../api/client';
import toast from 'react-hot-toast';
import OverviewTab      from './OverviewTab';
import CommandsTab      from './tabs/CommandsTab';
import PanelTab         from './tabs/PanelTab';
import UsersTab         from './tabs/UsersTab';
import LogsTab          from './tabs/LogsTab';
import FlowsTab         from './tabs/FlowsTab';
import ScheduledTab     from './tabs/ScheduledTab';
import AnalyticsTab     from './tabs/AnalyticsTab';
import ActivityTab      from './tabs/ActivityTab';
import EndpointsTab     from './tabs/EndpointsTab';
import TemplatesTab     from './tabs/TemplatesTab';
import StorageTab       from './tabs/StorageTab';
import AutomationsTab   from './tabs/AutomationsTab';
import BroadcastsTab     from './tabs/BroadcastsTab';
import CommerceTab      from './tabs/CommerceTab';
import WhatsAppAdminTab from './tabs/WhatsAppAdminTab';
import './BotDetailPage.css';

// ── Main page ─────────────────────────────────────────────────
// ── Bot header ────────────────────────────────────────────────
const BOT_HEAD_COLORS = ['#0E9F6E', '#2563EB', '#7C3AED', '#DB2777', '#EA580C', '#0891B2', '#4F46E5', '#059669'];
function botHeadColor(str = '') {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return BOT_HEAD_COLORS[Math.abs(h) % BOT_HEAD_COLORS.length];
}

function BotHeader({ bot, isActive, onToggle, onInbox }) {
  const [busy, setBusy] = useState(false);
  const initials = (bot.bName || 'B').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('');

  async function toggle() {
    if (busy) return;
    setBusy(true);
    await onToggle(!isActive);
    setBusy(false);
  }
  function copyId() {
    navigator.clipboard?.writeText(bot.bId).then(() => toast.success('Bot ID copied')).catch(() => {});
  }

  return (
    <header className="bdx-head">
      <div className="bdx-avatar" style={{ background: botHeadColor(bot.bName || bot.bId) }} aria-hidden="true">
        {initials}
        <span className="bdx-avatar-wa"><i className="fa-brands fa-whatsapp" /></span>
      </div>
      <div className="bdx-id">
        <h1>{bot.bName}</h1>
        <div className="bdx-meta">
          <span className={`bdx-status ${isActive ? 'is-live' : 'is-paused'}`}>
            <span className="bdx-status-dot" /> {isActive ? 'Live' : 'Paused'}
          </span>
          <button type="button" className="bdx-chip" onClick={copyId} title="Copy bot ID">
            <span className="bdx-chip-label">ID</span> {bot.bId} <i className="fa-regular fa-copy" />
          </button>
        </div>
      </div>
      <div className="bdx-actions">
        <button type="button" className="bdx-btn" onClick={onInbox}>
          <i className="fa-solid fa-inbox" /> <span>Inbox</span>
        </button>
        <button
          type="button"
          className={`bdx-switch ${isActive ? 'on' : ''} ${busy ? 'busy' : ''}`}
          onClick={toggle}
          role="switch"
          aria-checked={isActive}
          title={isActive ? 'Pause bot' : 'Activate bot'}
        >
          <span className="bdx-switch-track"><span className="bdx-switch-knob" /></span>
          <span className="bdx-switch-label">{isActive ? 'Bot on' : 'Bot off'}</span>
        </button>
      </div>
    </header>
  );
}

// ── Section tabs ──────────────────────────────────────────────
//  Desktop: "priority+" tab bar — shows as many tabs as fit, the rest
//  live in a "More" menu, and the open tab is always kept visible.
//  Mobile: swipeable pill row + an "All sections" bottom sheet.
const TAB_GROUPS = ['Build', 'Engage', 'Insights'];

function BotTabs({ tabs, pathname, navigate }) {
  const wrapRef    = useRef(null);
  const measureRef = useRef(null);
  const pillsRef   = useRef(null);
  const [fit, setFit]         = useState(tabs.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Which tab is open (longest matching path wins; Overview only on exact match)
  const clean = pathname.replace(/\/+$/, '');
  let activeIdx = 0;
  tabs.forEach((t, i) => {
    if (!t.path) return;
    if (clean === t.fullPath || clean.startsWith(t.fullPath + '/')) activeIdx = i;
  });

  // Measure how many tabs fit in the bar
  useLayoutEffect(() => {
    const wrap = wrapRef.current, meas = measureRef.current;
    if (!wrap || !meas) return undefined;
    const compute = () => {
      const kids = Array.from(meas.children);
      const moreEl = kids.find(el => el.dataset.more);
      // offsetWidth/clientWidth are layout pixels — unaffected by the
      // desktop `html { zoom: .9 }` in index.css, unlike getBoundingClientRect
      const widths = kids.filter(el => !el.dataset.more).map(el => el.offsetWidth + 2); // + row gap
      const style = getComputedStyle(wrap);
      const avail = wrap.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) - 4;
      const MORE = (moreEl ? moreEl.offsetWidth : 120) + 6;
      const total = widths.reduce((a, b) => a + b, 0);
      if (total <= avail) { setFit(tabs.length); return; }
      let used = 0, n = 0;
      for (const w of widths) { if (used + w + MORE > avail) break; used += w; n++; }
      setFit(Math.max(1, n));
    };
    compute();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(compute) : null;
    ro?.observe(wrap);
    window.addEventListener('resize', compute);
    return () => { ro?.disconnect(); window.removeEventListener('resize', compute); };
  }, [tabs.length]);

  let visible = tabs.map((_, i) => i).slice(0, fit);
  if (fit < tabs.length && activeIdx >= fit) visible = [...visible.slice(0, fit - 1), activeIdx];
  const hidden = tabs.map((_, i) => i).filter(i => !visible.includes(i));

  // Close menus on route change / Escape; lock scroll for the sheet
  useEffect(() => { setMoreOpen(false); setSheetOpen(false); }, [pathname]);
  useEffect(() => {
    if (!moreOpen && !sheetOpen) return undefined;
    const onKey = e => { if (e.key === 'Escape') { setMoreOpen(false); setSheetOpen(false); } };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    if (sheetOpen) document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [moreOpen, sheetOpen]);

  // Keep the active pill centred in the mobile scroller
  useEffect(() => {
    const row = pillsRef.current;
    const el = row?.querySelector('.bdx-pill.is-active');
    if (!row || !el) return;
    row.scrollTo({ left: el.offsetLeft - row.clientWidth / 2 + el.offsetWidth / 2, behavior: 'smooth' });
  }, [activeIdx]);

  const go = t => { navigate(t.fullPath); setMoreOpen(false); setSheetOpen(false); };

  const groupList = (indices, itemClass) => TAB_GROUPS.map(g => {
    const items = indices.filter(i => tabs[i].group === g);
    if (!items.length) return null;
    return (
      <div key={g} className="bdx-group">
        <div className="bdx-group-label">{g}</div>
        {items.map(i => {
          const t = tabs[i];
          return (
            <button key={t.path || 'overview'} type="button" className={`${itemClass} ${i === activeIdx ? 'is-active' : ''}`} onClick={() => go(t)}>
              <span className="bdx-item-ic"><i className={`fa-solid ${t.icon}`} /></span>
              <span>{t.label}</span>
              {i === activeIdx && <i className="fa-solid fa-check bdx-item-check" />}
            </button>
          );
        })}
      </div>
    );
  });

  return (
    <>
      {/* ── Desktop / tablet ── */}
      <div className="bdx-tabs" ref={wrapRef}>
        <div className="bdx-tabs-measure" ref={measureRef} aria-hidden="true">
          {tabs.map(t => <span key={t.path || 'overview'} className="bdx-tab"><i className={`fa-solid ${t.icon}`} />{t.label}</span>)}
          <span className="bdx-tab bdx-more-btn" data-more="1"><i className="fa-solid fa-ellipsis" />More <em>16</em><i className="fa-solid fa-chevron-down bdx-more-caret" /></span>
        </div>
        <nav className="bdx-tabs-row" aria-label="Bot sections">
          {visible.map(i => {
            const t = tabs[i];
            return (
              <NavLink key={t.path || 'overview'} to={t.fullPath} end={!t.path}
                className={() => `bdx-tab ${i === activeIdx ? 'is-active' : ''}`}>
                <i className={`fa-solid ${t.icon}`} />{t.label}
              </NavLink>
            );
          })}
          {hidden.length > 0 && (
            <div className="bdx-more">
              <button type="button" className={`bdx-tab bdx-more-btn ${moreOpen ? 'is-open' : ''}`} onClick={() => setMoreOpen(o => !o)} aria-expanded={moreOpen}>
                <i className="fa-solid fa-ellipsis" />More <em>{hidden.length}</em>
                <i className="fa-solid fa-chevron-down bdx-more-caret" />
              </button>
              {moreOpen && (
                <>
                  <div className="bdx-catch" onClick={() => setMoreOpen(false)} />
                  <div className="bdx-menu" role="menu">{groupList(hidden, 'bdx-menu-item')}</div>
                </>
              )}
            </div>
          )}
        </nav>
      </div>

      {/* ── Mobile ── */}
      <div className="bdx-mtabs">
        <div className="bdx-pills" ref={pillsRef}>
          {tabs.map((t, i) => (
            <button key={t.path || 'overview'} type="button" className={`bdx-pill ${i === activeIdx ? 'is-active' : ''}`} onClick={() => go(t)}>
              <i className={`fa-solid ${t.icon}`} />{t.label}
            </button>
          ))}
        </div>
        <button type="button" className="bdx-all" onClick={() => setSheetOpen(true)} aria-label="All sections">
          <i className="fa-solid fa-grip" />
        </button>
      </div>

      {sheetOpen && (
        <div className="bdx-sheet-backdrop" onClick={() => setSheetOpen(false)}>
          <div className="bdx-sheet" role="dialog" aria-label="Bot sections" onClick={e => e.stopPropagation()}>
            <span className="bdx-sheet-grip" />
            <div className="bdx-sheet-head">
              <b>Bot sections</b>
              <button type="button" className="bdx-sheet-close" onClick={() => setSheetOpen(false)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="bdx-sheet-body">{groupList(tabs.map((_, i) => i), 'bdx-sheet-item')}</div>
          </div>
        </div>
      )}

    </>
  );
}

export default function BotDetailPage() {
  const { bId }  = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [bot,      setBot]      = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [deleting, setDeleting] = useState(false);

  const loadBot = useCallback(() =>
    getBot(bId)
      .then(res => { if (res.data?.success) setBot(res.data.bot); })
      .catch(() => toast.error('Failed to load bot'))
      .finally(() => setLoading(false))
  , [bId]);

  useEffect(() => { loadBot(); }, [loadBot]);

  async function handleToggle(active) {
    try {
      await toggleBot(bId, active);
      setBot(b => ({ ...b, bIsActive: active ? 1 : 0 }));
      toast.success(active ? 'Bot activated' : 'Bot deactivated');
    } catch { toast.error('Failed to update bot'); }
  }

  // `confirmed` = the caller already showed its own confirmation (Overview's typed-name check).
  async function handleDelete(confirmed = false) {
    if (confirmed !== true && !window.confirm(`Delete "${bot?.bName}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deleteBot(bId);
      toast.success('Bot deleted');
      navigate('/dashboard');
    } catch { toast.error('Failed to delete bot'); setDeleting(false); }
  }

  // ── Tab definitions (grouped for the mobile sheet / More menu) ──
  const TABS = [
    { path: '',            label: 'Overview',    icon: 'fa-gauge-high',        group: 'Build'    },
    { path: 'commands',    label: 'Commands',    icon: 'fa-terminal',          group: 'Build'    },
    { path: 'flows',       label: 'Flows',       icon: 'fa-bolt',              group: 'Build'    },
    { path: 'automations', label: 'Automations', icon: 'fa-diagram-project',   group: 'Build'    },
    { path: 'templates',   label: 'Templates',   icon: 'fa-file-lines',        group: 'Build'    },
    { path: 'endpoints',   label: 'Endpoints',   icon: 'fa-plug',              group: 'Build'    },
    { path: 'panel',       label: 'Panel',       icon: 'fa-sliders',           group: 'Build'    },
    { path: 'storage',     label: 'Storage',     icon: 'fa-database',          group: 'Build'    },
    { path: 'broadcasts',  label: 'Broadcasts',  icon: 'fa-bullhorn',          group: 'Engage'   },
    { path: 'scheduled',   label: 'Scheduled',   icon: 'fa-calendar-days',     group: 'Engage'   },
    { path: 'commerce',    label: 'Commerce',    icon: 'fa-bag-shopping',      group: 'Engage'   },
    { path: 'phone-admin', label: 'Phone Admin', icon: 'fa-mobile-screen-button', group: 'Engage' },
    { path: 'users',       label: 'Users',       icon: 'fa-users',             group: 'Insights' },
    { path: 'analytics',   label: 'Analytics',   icon: 'fa-chart-line',        group: 'Insights' },
    { path: 'activity',    label: 'Activity',    icon: 'fa-wave-square',       group: 'Insights' },
    { path: 'logs',        label: 'Logs',        icon: 'fa-list-ul',           group: 'Insights' },
  ].map(t => ({ ...t, fullPath: t.path ? `/bots/${bId}/${t.path}` : `/bots/${bId}` }));

  if (loading) return (
    <div className="bot-detail bdx">
      <div className="bdx-skel">
        <span className="bdx-sk w20" />
        <div className="bdx-head"><span className="bdx-sk av" /><div style={{ flex: 1 }}><span className="bdx-sk w40 h22" /><span className="bdx-sk w30" /></div></div>
        <div className="bdx-sk bar" />
      </div>
    </div>
  );

  if (!bot) return (
    <div className="bot-detail bdx">
      <div className="bdx-notfound">
        <span><i className="fa-solid fa-robot" /></span>
        <h2>Bot not found</h2>
        <p>It may have been deleted, or you don’t have access to it.</p>
        <button className="bdx-btn" onClick={() => navigate('/dashboard')}><i className="fa-solid fa-arrow-left" /> Back to dashboard</button>
      </div>
    </div>
  );

  const isActive = bot.bIsActive == 1; // eslint-disable-line eqeqeq

  return (
    <div className="bot-detail bdx">
      <nav className="bdx-crumbs" aria-label="Breadcrumb">
        <button type="button" onClick={() => navigate('/dashboard')}><i className="fa-solid fa-arrow-left" /> Dashboard</button>
        <i className="fa-solid fa-chevron-right bdx-crumb-sep" />
        <span>{bot.bName}</span>
      </nav>

      <BotHeader bot={bot} isActive={isActive} onToggle={handleToggle} onInbox={() => navigate(`/inbox/${bId}`)} />

      <BotTabs tabs={TABS} pathname={location.pathname} navigate={navigate} />

      <div className="detail-content">
        <Routes>
          <Route index element={
            <OverviewTab bot={bot} onToggle={handleToggle} onDelete={handleDelete}
              onImportDone={loadBot} onBotUpdated={updated => setBot(updated)} />
          } />
          <Route path="commands"  element={<CommandsTab bId={bId} bot={bot} />} />
          <Route path="flows"     element={<FlowsTab bId={bId} bot={bot} />} />
          <Route path="automations/*" element={<AutomationsTab bId={bId} />} />
          <Route path="panel"     element={<PanelTab bId={bId} />} />
          <Route path="users"     element={<UsersTab bId={bId} />} />
          <Route path="logs"      element={<LogsTab bId={bId} />} />
          {/* the Menu tab was removed — old links land on the overview */}
          <Route path="menu"      element={<Navigate to={`/bots/${bId}`} replace />} />
          <Route path="scheduled" element={<ScheduledTab bId={bId} />} />
          <Route path="analytics" element={<AnalyticsTab bId={bId} />} />
          <Route path="activity"  element={<ActivityTab bId={bId} />} />
          <Route path="endpoints" element={<EndpointsTab bot={bot} />} />
          <Route path="templates" element={<TemplatesTab bot={bot} />} />
          <Route path="storage"   element={<StorageTab bId={bId} />} />
          <Route path="broadcasts" element={<BroadcastsTab bId={bId} />} />
          <Route path="commerce"  element={<CommerceTab bId={bId} />} />
          <Route path="phone-admin" element={<WhatsAppAdminTab bId={bId} bot={bot} />} />
          <Route path="icebreakers" element={<Navigate to={`/bots/${bId}`} replace />} />
          {/* Inbox moved out to its own top-level sidebar item */}
          <Route path="inbox"       element={<Navigate to={`/inbox/${bId}`} replace />} />
          <Route path="*"         element={<Navigate to={`/bots/${bId}`} replace />} />
        </Routes>
      </div>

      {deleting && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:999 }}>
          <span className="spinner spinner-dark" />
        </div>
      )}
    </div>
  );
}